import {
  useState,
  useReducer,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { Box } from "@mui/material";
import moment from "moment";
import axios from "axios";
import { apiUrl } from "features/configure";
import { useOutletContext, useParams } from "react-router";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { useIntl } from "react-intl";
import { get, isEmpty } from "lodash-es";

const useDAppTrackingState = () => {
  const [entityName] = useOutletContext();
  const { subEntityName } = useParams();
  const { formatMessage } = useIntl();

  const dynamicApiUrl = useMemo(() => {
    switch (true) {
      case !!subEntityName:
        return `apiEvaluate`;
      default:
        return `api`;
    }
  }, [subEntityName]);

  const dynamicEntityName = useMemo(() => {
    switch (true) {
      case !!subEntityName:
        return `${entityName}/${subEntityName}`;
      default:
        return entityName;
    }
  }, [entityName, subEntityName]);

  const [viewTarget, setViewTarget] = useState([""]);
  const [dateRange, setDateRange] = useState([
    moment().utc().startOf("day").subtract(6, "days").valueOf(),
    moment().utc().endOf("day").valueOf(),
  ]);
  const dispatchSnackbar = useDispatchSnackbar();

  const [contractOptions, setContractOptions] = useState([]);
  const [contractOptionsLoading, setContractOptionsLoading] = useState(false);

  const [basicData, setBasicData] = useState({ dataInfo: [] });
  const [basicDataLoading, setBasicDataLoading] = useState(true);

  const [walletActivityOverviewData, setWalletActivityOverviewData] = useState(
    []
  );
  const [
    walletActivityOverviewDataLoading,
    setWalletActivityOverviewDataLoading,
  ] = useState(true);

  const [newWalletData, setNewWalletData] = useState([]);
  const [newWalletDataLoading, setNewWalletDataLoading] = useState(true);

  const [cohortData, setCohortData] = useState({});
  const [cohortDataLoading, setCohortDataLoading] = useState(true);

  const fetchBasicData = async ({ viewTarget, from, to }) => {
    try {
      setBasicDataLoading(true);
      const { data } = await axios({
        method: "POST",
        url: `${apiUrl}/${dynamicApiUrl}/dAppTracking/${dynamicEntityName}/stats`,
        data: {
          from: from ? moment(from).valueOf() : undefined,
          to: to ? moment(to).valueOf() : undefined,
          viewTarget: viewTarget,
        },
      });

      setBasicData(data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setBasicDataLoading(false);
    }
  };

  const fetchContractOptions = async () => {
    try {
      setContractOptionsLoading(true);
      const { data } = await axios(
        apiUrl + `/${dynamicApiUrl}/dAppTracking/${dynamicEntityName}/contracts`
      );
      const optionsFormat = get(data, "contracts", []).map((contract) => {
        return { value: contract._id, label: contract.name };
      });

      setContractOptions([
        {
          label: formatMessage({ id: "global.filter.all" }),
          isAllOption: true,
          value: "",
        },
        ...optionsFormat,
      ]);
    } catch (err) {
      setContractOptions([
        {
          label: formatMessage({ id: "global.filter.all" }),
          isAllOption: true,
          value: "",
        },
      ]);
      dispatchSnackbar({ err });
    } finally {
      setContractOptionsLoading(false);
    }
  };

  const getTooltipText = useCallback(
    (id) => {
      switch (id) {
        case "uniqueWalletCount":
          return formatMessage({
            id: "metrics.uniqueWallet.tooltip",
          });
        case "transactions":
          return formatMessage({
            id: "metrics.transactions.tooltip",
          });
        case "avgTransactions":
          return formatMessage({
            id: "metrics.transactionAvg.tooltip",
          });

        default:
          return "";
      }
    },
    [formatMessage]
  );

  const formatPercentage = (number) => {
    return number === "-" ? "-" : `${(number * 100).toFixed(2)}%`;
  };

  const highLevelDataArray = useMemo(() => {
    return basicData?.dataInfo?.map(
      ({ id, dataTitle, number, totalNumber, rate }) => {
        let formattedNumber;

        if (id === "retentionRate" || id === "unifiedRetentionRate") {
          formattedNumber = formatPercentage(number);
        } else if (id === "avgTransactions") {
          formattedNumber =
            typeof number === "number" ? number.toFixed(2) : "-";
        } else {
          formattedNumber = typeof number === "number" ? number : "-";
        }

        return {
          dataTitle,
          tooltipText: getTooltipText(id),
          number: formattedNumber,
          totalNumber: totalNumber || undefined, // 若無此欄位則為 undefined
          rate,
          id,
        };
      }
    );
  }, [basicData?.dataInfo, getTooltipText]);

  const fetchWalletActivityOverviewData = async ({ viewTarget, from, to }) => {
    try {
      setWalletActivityOverviewDataLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/dAppTracking/${dynamicEntityName}/walletActivityOverview`,
        {
          from: moment(from).valueOf(),
          to: moment(to).valueOf(),
          viewTarget,
        }
      );

      setWalletActivityOverviewData(data.data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setWalletActivityOverviewDataLoading(false);
    }
  };

  const fetchNewWalletData = async ({ viewTarget, from, to }) => {
    try {
      setNewWalletDataLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/dAppTracking/${dynamicEntityName}/newWallets`,
        {
          from: moment(from).valueOf(),
          to: moment(to).valueOf(),
          viewTarget,
        }
      );

      setNewWalletData(data.data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setNewWalletDataLoading(false);
    }
  };

  const fetchCohortData = async ({ viewTarget, from, to }) => {
    try {
      setCohortDataLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/${dynamicApiUrl}/dAppTracking/${dynamicEntityName}/cohortRetentionAnalysis`,
        {
          from: moment(from).valueOf(),
          to: moment(to).valueOf(),
          viewTarget,
        }
      );
      setCohortData(data.data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setCohortDataLoading(false);
    }
  };

  return {
    dateRange,
    setDateRange,
    highLevelDataArray,
    viewTarget,
    setViewTarget,
    contractOptions,
    contractOptionsLoading,
    fetchContractOptions,
    basicData,
    basicDataLoading,
    newWalletData,
    newWalletDataLoading,
    walletActivityOverviewData,
    walletActivityOverviewDataLoading,
    cohortData,
    cohortDataLoading,
    fetchBasicData,
    fetchNewWalletData,
    fetchCohortData,
    fetchWalletActivityOverviewData,
  };
};

export default useDAppTrackingState;
